// HEAL-46b §9.0 (D-32) — строка «Уведомления» в Профиле, минимальный вариант.
//
// Три состояния, и ради третьего строка вообще существует: человек отказался в
// момент загрузки, а на iOS системное окно показывается один раз. Без этой
// строки дороги назад у него нет.
//
//   разрешение есть, подписан   → переключатель включён, выключение отписывает
//   разрешение есть, отписан    → переключатель выключен, включение подписывает
//   разрешения нет / отозвано   → переключатель неактивен + «Разрешить в
//                                 настройках телефона»
//
// Настроек по типам уведомлений нет: тип один (D-20, D-21 вынесли остальные).

import { useEffect, useState } from 'react';
import { Bell } from 'lucide-react';
import { App as CapacitorApp } from '@capacitor/app';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/api/client';
import { disablePush, enablePush, pushPermission, pushSupported, syncPushToken } from '@/lib/push';

type Props = {
  /** Значение с сервера: человек не отписан. */
  pushEnabled: boolean;
  onChanged?: () => void;
};

export function NotificationsRow({ pushEnabled, onChanged }: Props) {
  const [permission, setPermission] = useState<'granted' | 'denied' | 'prompt'>('prompt');
  const [enabled, setEnabled] = useState(pushEnabled);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    let alive = true;
    void pushPermission().then((p) => {
      if (alive) setPermission(p);
    });

    return () => {
      alive = false;
    };
  }, []);

  // На вебе строки нет вовсе: push в браузере не делаем (D-9), и показывать
  // неработающий переключатель значит обещать несуществующее.
  if (!pushSupported()) return null;

  const blockedBySystem = permission !== 'granted';

  const toggle = async (next: boolean) => {
    setBusy(true);
    const previous = enabled;
    setEnabled(next);

    try {
      await api.patch('/profile/notifications', { push_enabled: next });

      // Порядок важен: сначала сервер, потом устройство. Если упадёт запрос,
      // локально ничего не изменилось и состояния не разъехались.
      if (next) {
        await syncPushToken();
      } else {
        await disablePush();
      }

      onChanged?.();
    } catch {
      setEnabled(previous);
      toast({
        title: 'Не удалось изменить настройку',
        description: 'Попробуйте ещё раз.',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const askSystem = async () => {
    setBusy(true);
    const granted = await enablePush();
    setPermission(granted ? 'granted' : 'denied');

    if (granted) {
      await toggle(true);

      return;
    }

    // Второй раз система не спросит — остаются только её настройки.
    toast({
      title: 'Уведомления выключены в настройках телефона',
      description: 'Откройте настройки приложения и разрешите уведомления.',
      variant: 'warning',
    });
    setBusy(false);
  };

  return (
    <div className="flex w-full items-center gap-3 p-3 text-left">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary-text">
        <Bell className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold">Уведомления</div>
        <div className="text-hl-small text-muted-foreground">
          {blockedBySystem
            ? 'Разрешите в настройках телефона'
            : 'Сообщим, когда анализы будут обработаны'}
        </div>
      </div>
      {blockedBySystem ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => void askSystem()}
          className="shrink-0 text-sm font-medium text-primary-text"
        >
          Разрешить
        </button>
      ) : (
        <Switch
          checked={enabled}
          disabled={busy}
          onCheckedChange={(next) => void toggle(next)}
          aria-label="Уведомления о готовности анализов"
        />
      )}
    </div>
  );
}

/**
 * Разрешение могли выдать или отозвать в системных настройках, пока приложение
 * было свёрнуто. Возврат на передний план — единственный момент, когда это
 * можно заметить.
 */
export function useRefreshPermissionOnResume(onResume: () => void): void {
  useEffect(() => {
    if (!pushSupported()) return;

    const handle = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) onResume();
    });

    return () => {
      void handle.then((h) => h.remove());
    };
  }, [onResume]);
}
